import React, {forwardRef, ReactNode} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../core/src/helpers/browser/keyboardHelpers';
import {ThemeReader, useUserTheme} from '../../theme/userThemeProvider';
import {Icon, IconName} from '../icon/icon';
import {IconColor, resolveIconColor} from '../icon/iconColor';
import {KeyboardShortcuts} from '../keyboard/keyboardShortcuts';
import {SimpleTooltip} from '../tooltips/tooltipContent/simpleTooltip/simpleTooltip';
import {TooltipCoordinator, TooltipCoordinatorProps} from '../tooltips/tooltipCoordinator';
import {TooltipShortcutFormatter} from '../tooltips/tooltipShortcutFormatter';
import {VisuallyHidden} from '../visuallyHidden/visuallyHidden';
import {ChromelessButton, ChromelessButtonProps} from './chromelessButton';

/*
 * Props.
 */

const iconButtonSizes = {
  medium: {size: 30, borderRadius: 6},
  small: {size: 26, borderRadius: 6},
  xsmall: {size: 20, borderRadius: 4},
} as const;

export type IconButtonSize = keyof typeof iconButtonSizes;

export enum IconButtonVariant {
  /** Default - Transparent background, with semi-transparent hover and pressed states. */
  TERTIARY = 'tertiary',
  /** Filled background with primary color */
  PRIMARY = 'primary',

  /** AI Copilot variants */
  COPILOT_SECONDARY = 'copilot-secondary',
}

const iconButtonVariants: Record<
  IconButtonVariant,
  {
    defaultColor: ThemeReader;
    colorDisabled: ThemeReader;
    backgroundColor: ThemeReader;
    backgroundHoverColor: ThemeReader;
    backgroundActiveColor: ThemeReader;
    backgroundDisabledColor: ThemeReader;
  }
> = {
  [IconButtonVariant.TERTIARY]: {
    defaultColor: (theme) => theme.icon.primary,
    colorDisabled: (theme) => theme.icon.disabled,
    backgroundColor: (theme) => theme.background.fill.tertiary,
    backgroundHoverColor: (theme) => theme.background.fill.tertiaryHover,
    backgroundActiveColor: (theme) => theme.background.fill.tertiaryPressed,
    backgroundDisabledColor: (theme) => theme.background.fill.tertiaryDisabled,
  },
  [IconButtonVariant.COPILOT_SECONDARY]: {
    defaultColor: (theme) => theme.accent.blue.highlight,
    colorDisabled: (theme) => theme.icon.disabled,
    backgroundColor: (theme) => theme.accent.blue.surface,
    backgroundHoverColor: (theme) => theme.accent.blue.surfaceHover,
    backgroundActiveColor: (theme) => theme.accent.blue.highlight,
    backgroundDisabledColor: (theme) => theme.background.fill.primaryDisabled,
  },
  [IconButtonVariant.PRIMARY]: {
    defaultColor: (theme) => theme.icon['on-bg-fill'],
    colorDisabled: (theme) => theme.icon.disabled,
    backgroundColor: (theme) => theme.background.fill.primary,
    backgroundHoverColor: (theme) => theme.background.fill.primaryHover,
    backgroundActiveColor: (theme) => theme.background.fill.primaryPressed,
    backgroundDisabledColor: (theme) => theme.background.fill.primaryDisabled,
  },
};

export interface IconButtonProps extends ChromelessButtonProps {
  /** The icon to display. */
  iconName: IconName;

  hasIndicator?: boolean;

  /** @default "tertiary" */
  variant?: `${IconButtonVariant}`;

  /**
   * The primary icon color.
   * @default "primary" for tertiary variant, "on-bg-fill" for primary variant
   * @warning It is not recommended to use non-semantic icon colors, but if you need to, you should use a styled(IconColor);
   */
  color?: IconColor;

  /** Label to use for accessibility and in the tooltip. */
  label: ReactNode;

  /** @default "medium" */
  size?: IconButtonSize;

  /** Whether to render the icon inline. */
  isIconInline?: boolean;

  tooltipProps?: Partial<TooltipCoordinatorProps>;

  /** Keyboard shortcut for this button. */
  keyboardAction?: KeyboardActionsEnum;
  /** Override to _not_ bind keyboard shortcut automatically if a `keyboardAction` is passed. */
  shouldNotBindKeyboardAction?: boolean;

  /** Whether the tooltip is disabled. This enables adding a label but not adding a tooltip. */
  isTooltipDisabled?: boolean;
}

/*
 * Style.
 */

interface WrapperButtonProps {
  $size: IconButtonSize;
  $variant: `${IconButtonVariant}`;
  $color?: IconColor;
}

export const StyledIconButtonWrapper = styled(ChromelessButton)<WrapperButtonProps>`
  flex-shrink: 0;

  width: ${(p) => iconButtonSizes[p.$size].size}px;
  height: ${(p) => iconButtonSizes[p.$size].size}px;

  border-radius: ${(p) => iconButtonSizes[p.$size].borderRadius}px;

  display: flex;
  align-items: center;
  justify-content: center;

  background-color: ${(p) => iconButtonVariants[p.$variant].backgroundColor(p.theme)};
  color: ${(p) =>
    p.$color ? resolveIconColor(p.$color)(p) : iconButtonVariants[p.$variant].defaultColor(p.theme)};

  &:hover {
    background-color: ${(p) => iconButtonVariants[p.$variant].backgroundHoverColor(p.theme)};
  }

  &:active,
  &[aria-expanded='true'],
  &[aria-pressed='true'] {
    background-color: ${(p) => iconButtonVariants[p.$variant].backgroundActiveColor(p.theme)};
  }

  &[aria-disabled='true'] {
    background-color: ${(p) => iconButtonVariants[p.$variant].backgroundDisabledColor(p.theme)};
    color: ${(p) => iconButtonVariants[p.$variant].colorDisabled(p.theme)};
  }

  -webkit-app-region: no-drag;
`;

export const iconButtonPaddingPx = 4;

/*
 * Component.
 */

export const IconButton = forwardRef<HTMLDivElement, IconButtonProps>(
  (
    {
      className,
      label,
      tooltipProps,
      color,
      size = 'medium',
      variant = 'tertiary',
      isIconInline,
      iconName,
      keyboardAction,
      shouldNotBindKeyboardAction,
      hasIndicator,
      ...buttonProps
    },
    ref,
  ) => {
    const theme = useUserTheme();

    const button = (
      <TooltipCoordinator
        className={className}
        {...makeTooltipProps({...buttonProps, tooltipProps, label, keyboardAction})}
      >
        <StyledIconButtonWrapper $size={size} $variant={variant} $color={color} ref={ref} {...buttonProps}>
          <Icon
            name={iconName}
            isInline={isIconInline}
            indicatorColor={hasIndicator ? theme.indicatorColor : undefined}
          />
          <VisuallyHidden>{label}</VisuallyHidden>
        </StyledIconButtonWrapper>
      </TooltipCoordinator>
    );

    if (!keyboardAction || !buttonProps.onClick || buttonProps.isDisabled || shouldNotBindKeyboardAction) {
      return button;
    }

    // Otherwise, handle the keyboard shortcut.
    const keyHandlers = {[keyboardAction]: buttonProps.onClick};
    return <KeyboardShortcuts handlers={keyHandlers}>{button}</KeyboardShortcuts>;
  },
);

function makeTooltipProps(
  props: Pick<
    IconButtonProps,
    'label' | 'keyboardAction' | 'hasHover' | 'isTooltipDisabled' | 'isOpen' | 'tooltipProps'
  >,
): TooltipCoordinatorProps {
  if (props.hasHover || props.isTooltipDisabled || props.isOpen) {
    return {
      ...props.tooltipProps,
      render: undefined,
      plainTooltip: undefined,
    };
  }

  if (props.tooltipProps?.render || props.tooltipProps?.plainTooltip || !props.label) {
    return props.tooltipProps ?? {};
  }

  const labelTooltip = (
    <SimpleTooltip>
      {props.label}
      {props.keyboardAction && (
        <>
          &nbsp;
          <TooltipShortcutFormatter action={props.keyboardAction} />
        </>
      )}
    </SimpleTooltip>
  );

  return {
    ...props.tooltipProps,
    plainTooltip: labelTooltip,
  };
}
